import React, { useState, useMemo, useEffect } from "react"
import {
    Box,
    FormControl,
    Select,
    MenuItem,
    ListSubheader,
    TextField,
    InputAdornment,
    Chip,
    Checkbox
} from "@mui/material"
import { KeyboardArrowDown, Search } from "@mui/icons-material"
import {
    sxSelect,
    sxMenuPaper,
    sxChipSelect,
    sxBoxRenderValue
} from "./SxOneAutocomplete"
import { empty } from "../../Utils/Utils"

const filterOptionsBySearchText = (text, searchText) =>
    text.toLowerCase().includes(searchText.toLowerCase())

export default function OneAutocomplete({
    name,
    options,
    label = "descricao",
    valor = "id",
    required = false,
    onChange,
    defaultValue = "", // "" como padrão inicial
    newSx = {},
    todos = false,
    separado = false,
    reset = false,
    multiple = false,
    isThreshold = false,
    onThresholdFetch, // Função para Threshold
    ...rest
}) {
    const [searchText, setSearchText] = useState("")
    const [selectedValue, setSelectedValue] = useState(() => {
        if (multiple) {
            if (!empty(defaultValue)) {
                return [defaultValue]
            }
            return []
        }
        return defaultValue
    })
    const [thresholdOptions, setThresholdOptions] = useState([])

    const enhancedOptions = useMemo(() => {
        if (todos && !options.some((opt) => opt[valor] === '')) {
            return [{ id: '', descricao: "Todos" }, ...options]
        }
        return options
    }, [options, todos, valor])

    const displayedOptions = useMemo(() => {
        if (isThreshold && searchText.length < 3) {
            return []
        }
        const filteredOptions = enhancedOptions.filter((option) =>
            filterOptionsBySearchText(option[label], searchText)
        )
        return isThreshold ? thresholdOptions : filteredOptions
    }, [searchText, enhancedOptions, label, isThreshold, thresholdOptions])

    useEffect(() => {
        if (reset) {
            setSearchText("")
            setSelectedValue(multiple ? [] : '')
        }
    }, [reset, multiple])

    // Fetch Threshold options
    useEffect(() => {
        if (isThreshold && searchText.length >= 3 && onThresholdFetch) {
            const fetchThresholdOptions = async () => {
                const result = await onThresholdFetch(searchText)
                setThresholdOptions(result || [])
            }
            fetchThresholdOptions()
        }
    }, [searchText, isThreshold, onThresholdFetch])

    const handleSelectChange = (event) => {
        const { value } = event.target

        if (Array.isArray(value)) {
            if (value.length > 1 && value[0] === '' && value[1] !== '') {
                setSelectedValue(value.filter(selected => selected !== ''))
            } else {
                if (value.filter(selected => selected === '').length > 0) {
                    setSelectedValue([''])
                } else {
                    setSelectedValue(value)
                }
            }
        } else {
            setSelectedValue(multiple ? value : value)
        }
    }

    useEffect(() => {
        if (onChange) onChange({ target: { value: selectedValue } })
    }, [selectedValue])

    const renderValueSelect = React.useMemo(() => (selected, multiple, valor, enhancedOptions) => {
        if (multiple) {
            return (
                <Box sx={sxBoxRenderValue} display="flex" flexWrap="wrap">
                    {selected.length === 0
                        ? (<Chip sx={sxChipSelect} label="Nenhuma seleção" />)
                        : (
                            selected.map((value, index) => (
                                <Chip key={index} sx={sxChipSelect} label={enhancedOptions.find(option => option.id === value)?.descricao} />
                            ))
                        )}
                </Box>
            )
        } else {
            return (enhancedOptions.find((opt) => opt[valor] === selected)?.[label] || "Selecione...")
        }
    }, [])
    return (
        <Box>
            {enhancedOptions && (
                <FormControl
                    sx={{
                        width: "100%",
                        color: "black",
                        border: required
                            ? "1px solid #f44336"
                            : "1px solid #fff",
                        borderRadius: "5px"
                    }}
                >
                    <Select
                        name={name}
                        multiple={multiple}
                        sx={{ ...sxSelect, ...newSx }}
                        value={selectedValue}
                        onChange={handleSelectChange}
                        renderValue={(selected) => { return renderValueSelect(selected, multiple, valor, enhancedOptions) }}
                        MenuProps={{
                            autoFocus: false,
                            PaperProps: {
                                sx: {
                                    ...sxMenuPaper,
                                    position: "absolute",
                                    zIndex: 1300
                                }
                            },
                            container: () => document.getElementById("root"),
                            getContentAnchorEl: null,
                            anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left"
                            },
                            transformOrigin: {
                                vertical: "top",
                                horizontal: "left"
                            }
                        }}
                        PopperProps={{
                            modifiers: [
                                {
                                    name: "offset",
                                    options: {
                                        offset: [0, 4]
                                    }
                                },
                                {
                                    name: "preventOverflow",
                                    options: {
                                        boundary: "viewport"
                                    }
                                }
                            ]
                        }}
                        inputProps={{
                            sx: { padding: "0 5px" }
                        }}
                        IconComponent={KeyboardArrowDown}
                        onClose={() => setSearchText("")}
                        {...rest}
                    >
                        <ListSubheader>
                            <TextField
                                size="small"
                                placeholder={isThreshold ? "Digite pelo menos 3 caracteres para buscar" : "Pesquisar..."}
                                fullWidth
                                autoFocus
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    )
                                }}
                                onChange={(e) =>
                                    setSearchText(e.target.value)
                                }
                                onKeyDown={(e) => {
                                    if (e.key !== "Escape") {
                                        e.stopPropagation()
                                    }
                                }}
                            />
                        </ListSubheader>
                        {displayedOptions.map((option, index) => (
                            <MenuItem
                                key={`menu_combo_${index}`}
                                value={multiple ? option[valor] : option[valor]}
                            >
                                {multiple && (
                                    <Checkbox
                                        checked={Array.isArray(selectedValue) ? selectedValue.includes(option[valor]) : selectedValue === option[valor]}
                                        sx={{
                                            "&.Mui-checked": {
                                                color: "#6f0b81"
                                            },
                                            "&.MuiCheckbox-indeterminate": {
                                                color: "#6f0b81"
                                            }
                                        }}
                                    />
                                )}{!separado ? (<span>{option[label]}</span>) : (<><span style={{ width: "45px" }}>{option[valor]}</span><span> - {option[label]}</span></>)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </Box>
    )
}
